import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, User, ArrowLeft, ArrowRight } from 'lucide-react'
import { useApp } from '../context/app-context'
import { t } from '../i18n'
import { articles } from '../data/articles'
import { useParallax } from '../hooks/useParallax'

const categories = {
  ar: [
    { key: 'all', label: 'الكل' },
    { key: 'land', label: 'الشحن البري' },
    { key: 'customs', label: 'الجمارك' },
  ],
  en: [
    { key: 'all', label: 'All' },
    { key: 'land', label: 'Land Shipping' },
    { key: 'customs', label: 'Customs' },
  ],
}

export default function Articles() {
  const { lang } = useApp()
  const isAr = lang === 'ar'
  const [activeCategory, setActiveCategory] = useState('all')
  const ArrowIcon = isAr ? ArrowLeft : ArrowRight

  const filtered =
    activeCategory === 'all' ? articles : articles.filter((a) => a.category === activeCategory)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const parallaxRef = useParallax()

  return (
    <main className="page articles-page" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section
        className="page-hero articles-hero page-rise"
      >
        <div className="page-hero-overlay" />
        <img
          ref={parallaxRef}
          src="/assets/images/splash-truck.jpg"
          alt="articles"
          className="page-hero-img parallax-img"
        />
        <div className="page-hero-content">
          <h1>{t(lang, 'articlesHeroTitle')}</h1>
          <p>{t(lang, 'articlesHeroDesc')}</p>
        </div>
      </section>

      {/* Category Filter */}
      <div className="articles-filter-wrap">
        <div className="articles-filter">
          {categories[lang].map((cat) => (
            <button
              key={cat.key}
              className={`filter-btn ${activeCategory === cat.key ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <section className="section articles-section">
        <div className="section-inner">
          <div className="articles-grid">
            {filtered.map((article, i) => (
              <article
                key={`${activeCategory}-${article.id}`}
                className="article-card page-rise"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <Link
                  to={`/articles/${article.id}`}
                  className="article-card-link"
                  aria-label={isAr ? article.titleAr : article.titleEn}
                >
                <div className="article-img-wrap">
                  <img
                    src={article.image}
                    alt={isAr ? article.titleAr : article.titleEn}
                    className="article-img"
                  />
                  <div className="article-category-badge">
                    {categories[lang].find((c) => c.key === article.category)?.label}
                  </div>
                </div>
                <div className="article-content">
                  <div className="article-meta">
                    <span className="article-meta-item">
                      <User size={13} />
                      {isAr ? article.authorAr : article.authorEn}
                    </span>
                    <span className="article-meta-item">
                      <Clock size={13} />
                      {article.readTime} {t(lang, 'minRead')}
                    </span>
                  </div>
                  <h3 className="article-title">
                    {isAr ? article.titleAr : article.titleEn}
                  </h3>
                  <p className="article-excerpt">
                    {isAr ? article.excerptAr : article.excerptEn}
                  </p>
                  <div className="article-footer">
                    <span className="article-date">{formatDate(article.date)}</span>
                    <span className="article-read-more" aria-hidden="true">
                      {t(lang, 'readMore')} <ArrowIcon size={14} />
                    </span>
                  </div>
                </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
